import { useIntl } from "react-intl";
import { newCondition, type ConditionByTypename, type MatchCondition } from "../design-codec";
import { PlusButton } from "@/components/design/PlusButton";
import {
  CompositionEditor,
  OperatorSelect,
  RequirementControls,
  RowShell,
  SubsectionHeader,
  TextValueInput,
} from "./row-primitives";

type ExperienceCondition = ConditionByTypename<"ExperienceCondition">;
type Row = ExperienceCondition;

const EXPERIENCE_OPERATORS: ExperienceCondition["operator"][] = ["EQ", "NE"];

export interface FreitextSectionProps {
  conditions: Row[];
  onChange: (next: MatchCondition[]) => void;
  readOnly?: boolean;
  // Muss/Kann-Schloss je Zeile anzeigen (spark #151, @adr SPARK-0030-match-result-scoring-atom-derived@1: Attribut in allen Modi).
  showRequirement?: boolean;
}

export function FreitextSection({
  conditions,
  onChange,
  readOnly,
  showRequirement,
}: FreitextSectionProps) {
  const intl = useIntl();
  function patch(index: number, next: Row) {
    const updated = conditions.slice();
    updated[index] = next;
    onChange(updated as MatchCondition[]);
  }
  function remove(index: number) {
    const updated = conditions.slice();
    updated.splice(index, 1);
    onChange(updated as MatchCondition[]);
  }
  function addJobtitle() {
    onChange([
      ...(conditions as MatchCondition[]),
      newCondition("ExperienceCondition", {
        value: "",
        aiMode: "NO_AI",
        operator: "EQ",
      }),
    ]);
  }

  return (
    <div>
      <SubsectionHeader
        title={intl.formatMessage({ id: "match.freitext.title", defaultMessage: "Work Experience" })}
      />
      {/* Kennungen wie in DetailsSection: Zeile `requirement-row-<typ>-<n>`,
          Bedienelemente `requirement-<typ>-<n>-<rolle>` — zwei Präfixe, damit ein
          Wähler auf „alle Zeilen dieses Typs" nicht die Kinder mittrifft.
          Der Abschnitt führt nur einen Typ, der Zähler ist also der Listenindex. */}
      {conditions.map((c, i) => (
        <RowShell
          key={i}
          id={`requirement-row-jobtitlecondition-${i}`}
          removeId={`requirement-jobtitlecondition-${i}-remove`}
          onRemove={() => remove(i)}
          readOnly={readOnly}
        >
          {showRequirement ? (
            <RequirementControls
              id={`requirement-jobtitlecondition-${i}-lock`}
              requirement={c.requirement}
              readOnly={readOnly}
              onChange={(req) => patch(i, { ...c, requirement: req })}
            />
          ) : null}
          <TextValueInput
            id={`requirement-jobtitlecondition-${i}-value`}
            value={c.value}
            readOnly={readOnly}
            placeholder={intl.formatMessage({ id: "match.freitext.jobTitlePlaceholder", defaultMessage: "e.g. Senior Designer, Lead Engineer" })}
            onChange={(next) => patch(i, { ...c, value: next })}
          />
          <OperatorSelect
            id={`requirement-jobtitlecondition-${i}-operator`}
            value={c.operator}
            allowed={EXPERIENCE_OPERATORS}
            readOnly={readOnly}
            onChange={(op) => patch(i, { ...c, operator: op })}
          />
          <CompositionEditor
            id={`requirement-jobtitlecondition-${i}-composition`}
            value={c.composition}
            readOnly={readOnly}
            onChange={(comp) => patch(i, { ...c, composition: comp })}
          />
        </RowShell>
      ))}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-2">
        {!readOnly && <PlusButton id="requirement-add-jobtitlecondition" label={intl.formatMessage({ id: "match.freitext.addJobTitle", defaultMessage: "Job title" })} onClick={addJobtitle} />}
      </div>
    </div>
  );
}
